// src/components/shared/EnduserProtectedRoute.tsx

import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useEnduserAuth } from "../../contexts/EnduserAuthContext";

interface EnduserProtectedRouteProps {
  children: React.ReactNode;
  requireCalculatorAccess?: boolean; // אם true, בודק גם מנוי
  fallbackPath?: string; // לאן להפנות אם אין גישה
}

const EnduserProtectedRoute: React.FC<EnduserProtectedRouteProps> = ({
  children,
  requireCalculatorAccess = false,
  fallbackPath = "/enduser/login",
}) => {
  const {
    isAuthenticated,
    isLoading,
    authError,
    canAccessCalculators,
    refreshAuth,
  } = useEnduserAuth();

  const [initializing, setInitializing] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // אם עדיין לא אותחל, ננסה לרענן
        if (!isAuthenticated && !authError) {
          await refreshAuth();
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        setInitializing(false);
      }
    };

    initializeAuth();
  }, [isAuthenticated, authError, refreshAuth]);

  // Loading state
  if (isLoading || initializing) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        dir="rtl"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Authentication error - show error page
  if (authError) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        dir="rtl"
      >
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-600 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">שגיאה באימות</h2>
          <p className="text-gray-600 mb-4">{authError}</p>
          <button
            onClick={() => (window.location.href = "/enduser/login")}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            חזור להתחברות
          </button>
        </div>
      </div>
    );
  }

  // Check calculator access if required
  if (requireCalculatorAccess && !canAccessCalculators()) {
    // User authenticated but no calculator access - redirect to subscription page
    return (
      <Navigate to="/enduser/subscription" state={{ from: location }} replace />
    );
  }

  // All good - render protected content
  return <>{children}</>;
};

export default EnduserProtectedRoute;
